<template>
  <div>
    <base-header class="pb-6 bg-gradient-dark">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Affiliate List</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-breadcrumb></route-breadcrumb>
          </nav>
        </div>
      </div>
      <!-- Card stats -->
      <div class="row">
        <div class="col-xl-3 col-md-6">
          <stats-card
            title="Affiliate Info"
            type="gradient-info"
            :sub-title="`${referrals.length}`"
          >
            <h5 class="card-title text-uppercase text-muted mb-0">
              Affiliate Info
            </h5>
            <div class="card-body pl-0 pb-0" v-if="loader && !loader.isActive">
              <div class="font-weight-600">{{ affiliateInfo.name }}</div>
              <div>{{ affiliateInfo.email }}</div>
              <div
                v-if="affiliateInfo.address && affiliateInfo.address.address"
                class="pt-2"
              >
                <div class="h5 m-0">Address</div>
                <div>{{ affiliateInfo.address.address }}</div>
                <div>{{ affiliateInfo.address.country }}</div>
              </div>
              <div v-else class="text-sm pt-2">
                No address information! Please make sure to fill it in!
              </div>
            </div>
            <div
              v-if="affiliateInfo.email"
              class="d-flex justify-content-start pt-2"
            >
              <a
                class="btn btn-default btn-sm"
                :href="`https://www2.chinesepod.com/marketingcenter/users/index/view?user_id=${affiliateInfo.id}&site_id=2`"
                title="CRM Link"
                ><i class="fas fa-list"></i> CRM Link</a
              >
              <base-button type="primary" size="sm" @click="modal.show = true"
                ><i class="fas fa-cog"></i> Edit</base-button
              >
            </div>
          </stats-card>
        </div>
        <div
          v-for="event in uniqueEvents()"
          :key="event"
          class="col-xl-3 col-md-6"
        >
          <stats-card
            :title="`${event}s`"
            type="gradient-info"
            :sub-title="`${
              referrals.filter((i) => i.event_type === event).length
            }`"
            icon="fas fa-users"
          >
          </stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            title="fees due"
            type="gradient-info"
            :sub-title="calculateFeesDue()"
            icon="fas fa-money-bill-wave"
          >
          </stats-card>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!--Widgets-->

      <div class="row">
        <div class="col-12 col-lg-4 order-lg-2">
          <period-selection :date-filters="dateFilter" @change="handleChange" />
          <div v-if="referrals.length" :key="referrals.length">
            <referral-summary :referrals="referrals" />
            <referral-chart :referrals="referrals" />
          </div>
        </div>
        <div class="col-12 col-lg-8 order-lg-1">
          <referral-table
            :referrals="referrals"
            @generate-credit-note="generateCreditNote"
          />
          <affiliate-examples
            v-if="affiliateInfo && affiliateInfo.tag"
            :tag="affiliateInfo.tag"
          />
        </div>
      </div>
      <!--End Widgets-->
    </div>
    <affiliate-information-modal
      :show.sync="modal.show"
      :affiliate-info="affiliateInfo"
      :loading.sync="modal.loading"
      @submit="updateInformation"
      @close="modal.show = false"
    />
  </div>
</template>

<script>
import moment from 'moment'

import ReferralTable from './ReferralTable'
import PeriodSelection from '@/components/PeriodSelection'
import ReferralSummary from '@/components/ReferralSummary'
import ReferralChart from '@/components/ReferralChart'
import AffiliateInformationModal from '../../../../components/AffiliateInformationModal'
import AffiliateExamples from '@/components/AffiliateExamples'

export default {
  name: 'AffiliateAdminDetails',
  components: {
    AffiliateExamples,
    AffiliateInformationModal,
    ReferralChart,
    ReferralSummary,
    ReferralTable,
    PeriodSelection
  },
  data() {
    return {
      loader: false,
      modal: { show: false, loading: false },
      affiliateInfo: {},
      referrals: [],
      dateFilter: [
        moment().subtract(30, 'days').startOf('day').toDate(),
        moment().startOf('day').toDate()
      ]
    }
  },
  methods: {
    async handleChange(range) {
      if (
        range.length === 2 &&
        (moment(range[0]).unix() !== moment(this.dateFilter[0]).unix() ||
          moment(range[1]).unix() !== moment(this.dateFilter[1]).unix())
      ) {
        this.dateFilter = range
        await this.getAffiliateData()
      }
    },
    downloadFile(contentBase64, fileName) {
      const linkSource = `data:application/pdf;base64,${contentBase64}`
      const downloadLink = document.createElement('a')
      document.body.appendChild(downloadLink)

      downloadLink.href = linkSource
      downloadLink.target = '_self'
      downloadLink.download = fileName
      downloadLink.click()
    },
    async generateCreditNote(rows) {
      this.loader = this.$loading.show()
      await this.axios
        .post(process.env.VUE_APP_ROOT_API + '/api/v1/affiliates/invoices', {
          id: this.$route.params.id,
          events: rows.map((i) => i.event_id)
        })
        .then(({ data }) => {
          const { fileName, invoice } = data
          this.downloadFile(invoice, fileName)
          this.loader.hide()
          this.getAffiliateData()
        })
        .catch(() =>
          this.$notify({ type: 'danger', message: 'Something went wrong!' })
        )
      this.loader.hide()
    },
    async getAffiliateData() {
      this.loader = this.$loading.show()
      const [fromDate, toDate] = this.dateFilter
      await this.axios
        .post(
          process.env.VUE_APP_ROOT_API +
            '/api/v1/affiliates/users/' +
            this.$route.params.id,
          {
            fromDate,
            toDate
          }
        )
        .then(({ data }) => {
          this.referrals = data.events
          this.affiliateInfo = data
        })
        .catch(() =>
          this.$notify({ type: 'danger', message: 'Something went wrong!' })
        )
      this.loader.hide()
    },
    async updateInformation(affiliate) {
      this.modal.loading = true
      await this.axios
        .put(
          process.env.VUE_APP_ROOT_API +
            '/api/v1/affiliates/users/' +
            this.$route.params.id,
          affiliate
        )
        .then(({ data }) => {
          this.affiliateInfo = data
          this.modal.show = false
        })
        .catch(() =>
          this.$notify({ type: 'danger', message: 'Something went wrong!' })
        )
      this.modal.loading = false
    },
    uniqueEvents() {
      return [...new Set(this.referrals.map((i) => i.event_type))].sort()
    },
    calculateFeesDue() {
      const unpaid = this.referrals.filter(
        (i) => !i.invoice || i.invoice.status !== 'paid'
      )

      return (
        '$' +
        (unpaid.length
          ? unpaid.map((i) => i.event_fee).reduce((a, b) => a + b)
          : 0
        ).toFixed(2)
      )
    }
  },
  mounted() {
    this.getAffiliateData()
  }
}
</script>

<style scoped></style>
